import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { FilterField, labelId } from "kontrolEdge/helpers/advancedFilters";
import { SyslogItem } from "./request/request";

export const LOG_LEVELS = [
  "Emergency",
  "Alert",
  "Critical",
  "Error",
  "Warning",
  "Notice",
  "Informational",
  "Debug",
];

/**
 * Filter field specifications for syslog search
 */
export function makeSyslogFields(): FilterField<SyslogItem>[] {
  const fields: FilterField<SyslogItem>[] = [
    {
      label: "IP",
      key: "ip",
      accessor: (r) => r.ip,
      type: "string",
      inFreeText: true,
    },
    {
      label: "Name",
      key: "name",
      accessor: (r) => r.description || "",
      type: "string",
      inFreeText: true,
    },
    {
      label: "Port",
      key: "port",
      accessor: (r) => r.port.toString(),
      type: "string",
      inFreeText: true,
    },
    {
      label: "ID",
      key: "id",
      accessor: (r) => r.id.toString(),
      type: "string",
      inFreeText: true,
    },
  ];

  // Ensure keys are set
  for (const f of fields) f.key ||= labelId(f.label);
  return fields;
}

/**
 * Columns for the "Available" table
 * Shows a checkbox, IP, Port, and Name
 */
export function getAvailableColumns(
  selectedIds: Set<number>,
  onToggleRow: (id: number) => void
): GenericTableColumn<SyslogItem>[] {
  return [
    {
      header: "Select",
      renderCell: (row: SyslogItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          <GenericCheckbox
            checked={selectedIds.has(row.id)}
            onChange={() => onToggleRow(row.id)}
            size="sm"
          />
        </div>
      ),
    },
    {
      header: "IP",
      accessor: "ip",
      sortable: true,
    },
    {
      header: "Port",
      accessor: "port",
      sortable: true,
    },
    {
      header: "Name",
      accessor: "description",
      sortable: true,
    },
  ];
}

/**
 * Columns for the "Selected" table
 * Same as Available, plus a "Level" column with a dropdown
 */
export function getSelectedColumns(
  selectedIds: Set<number>,
  onToggleRow: (id: number) => void,
  onLevelChange: (row: SyslogItem, newLevelStr: string) => void
): GenericTableColumn<SyslogItem>[] {
  return [
    {
      header: "Select",
      renderCell: (row: SyslogItem) => (
        <div onClick={(e) => e.stopPropagation()}>
          <GenericCheckbox
            checked={selectedIds.has(row.id)}
            onChange={() => onToggleRow(row.id)}
            size="sm"
          />
        </div>
      ),
    },
    {
      header: "IP",
      accessor: "ip",
      sortable: true,
    },
    {
      header: "Port",
      accessor: "port",
      sortable: true,
    },
    {
      header: "Name",
      accessor: "description",
      sortable: true,
    },
    {
      header: "Level",
      renderCell: (row: SyslogItem) => {
        const currentLevelStr =
          row.level !== undefined ? LOG_LEVELS[row.level] : LOG_LEVELS[0];

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <GenericDropdown
              items={LOG_LEVELS}
              value={currentLevelStr}
              onSelect={(newVal) => onLevelChange(row, newVal)}
              widthClass="w-[140px]"
              fixedPosition={true}
              dropdownMaxHeightClass="max-h-48"
              size="sm"
            />
          </div>
        );
      },
    },
  ];
}

